import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { APIWeb } from '@/api'
import RefreshList from '@/components/RefreshList'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { toFixedNoRound, formatThousands, formatDateTime } from '@/utils'

export default React.memo(function TransferRecord() {
  const { t } = useTranslation()
  const [data, setData] = useState<ModelsMemberTransfer[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 通用的数据加载函数
  const fetchTransferRecord = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const result = await APIWeb.getTransferRecord({
        page,
        page_size: pageSize
      })

      if (result?.list) {
        const newData = result.list

        if (isAppend) {
          // 追加数据（用于加载更多）
          setData(prevData => [...prevData, ...newData])
        } else {
          // 替换数据（用于初始加载和刷新）
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2) // 初始加载后，下次从第2页开始
          } else {
            setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
          }
        }
      } else {
        if (isAppend) {
          setHasMore(false)
        } else {
          setData([])
          setHasMore(false)
        }
      }
    } catch (error) {
      console.error('获取转账记录失败:', error)
      setHasMore(false) // 发生错误时停止加载更多
    } finally {
      setLoading(false)
    }
  }, [loading])

  // 加载更多数据
  const loadMore = useCallback(async () => {
    await fetchTransferRecord(currentPage, true)
  }, [fetchTransferRecord, currentPage])

  // 下拉刷新处理函数
  const onRefresh = useCallback(async () => {
    await fetchTransferRecord(1, false)
  }, [fetchTransferRecord])

  const renderItem = useCallback((item: ModelsMemberTransfer) => {
    const isIn = item.transfer_type === 'in'
    return (
      <div className="px-6 py-4 border-b border-[#F5F5F5]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <span className="text-base font-medium text-black">
              {isIn ? t('转入') : t('转出')}
            </span>
          </div>
          <span className="text-3 text-[#979797]">
            {formatDateTime(item.created_at)}
          </span>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="text-3.5 text-[#979797]">
            {t('转账ID')}: {isIn ? item.receiver_xid : item.sender_xid}
          </div>
          <div className={`text-base font-semibold ${isIn ? 'text-[#FF4D4F]' : 'text-[#FF4D4F]'}`}>
            {isIn ? '+' : '-'}{formatThousands(item.amount || 0, 2, true)} {item.pay_type.toUpperCase()}
          </div>
        </div>

        <div className="text-3.5 text-[#979797]">
          {isIn ? t('发起方') : t('接收方')}: {isIn ? item.sender_xid : item.receiver_xid}
        </div>

        {item.remark && (
          <div className="mt-2 text-3 text-[#979797]">
            {t('备注')}: {item.remark}
          </div>
        )}
      </div>
    )
  }, [t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('转账记录')}</span>}
      />

      <div className="bg-white flex flex-col flex-1">
        <RefreshList
          data={data}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          onRefresh={onRefresh}
          onLoadMore={loadMore}
          loading={loading}
          hasMore={hasMore}
          className='flex-1'
        />
      </div>
    </PageLayout>
  )
})
